import axios from "axios";
import {
  retrieveRefreshToken,
  storeSessionToken,
} from "../../utils/sessionKeyManager.js";

const axiosClient = axios.create({
  baseURL: process.env.REACT_APP_BASE_API_URL,
  timeout: 30000,
});

/**
 * Token refresh interceptor.
 *
 * When any request returns 401 (access token expired), this interceptor
 * attempts a silent token refresh using the stored refresh token. If
 * successful, the original request is retried with the new access token.
 * If refresh fails (refresh token also expired), the user is redirected
 * to the login page.
 *
 * The refresh token is single-use: each refresh returns a new refresh
 * token and invalidates the old one. Both are re-encrypted and stored.
 */
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

axiosClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("/oauth/access_token") &&
      !originalRequest.url?.includes("/login")
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = "Bearer " + token;
            return axiosClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = retrieveRefreshToken();
      if (!refreshToken) {
        isRefreshing = false;
        processQueue(error, null);
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(
          process.env.REACT_APP_BASE_API_URL + "/oauth/access_token",
          "grant_type=refresh_token&refresh_token=" +
            encodeURIComponent(refreshToken),
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          },
        );

        if (response.status === 200 && response.data.access_token) {
          const newAccessToken = response.data.access_token;
          const newRefreshToken = response.data.refresh_token;

          storeSessionToken(newAccessToken, newRefreshToken);

          originalRequest.headers.Authorization = "Bearer " + newAccessToken;
          processQueue(null, newAccessToken);

          return axiosClient(originalRequest);
        } else {
          processQueue(error, null);
          return Promise.reject(error);
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default axiosClient;
